import type { AppState } from '@/core/state';
import type { Processor, Store } from '@/core/store';
import type { StudioEvent } from '@/core/events';
import type { Attempt, Notification, SkillView } from '@/core/types';
import { catalog } from '@/data/catalog';
import { BOSSES } from '@/data/progression';
import { dayKey, uid } from '@/core/util/misc';
import { applyEvidence, deriveView, newEstimate } from './estimates';
import { eligibilityOf, viewsOf } from './views';
import { skillRelevance } from './eligibility';
import { guessRate } from './questions/evaluate';
import { createOrUpdateMistake, progressRecovery } from './mistakes';
import { achievementById, checkAchievements, levelInfo, refreshQuests, xpForAnswer } from './progression';
import { review } from './srs';
import { mean } from './ability';
import { L, LL } from '@/i18n';

/**
 * PROCESSORS — the integration hub. A single answered question flows through:
 * attempt → estimate/mastery → confidence → mistakes → forgetting (memory) →
 * prerequisite signals → XP → quests → achievements → notifications.
 * Recommendations, the Knowledge Map and analytics are derived from the
 * resulting state, so they update automatically.
 */

const notify = (s: AppState, n: Omit<Notification, 'id' | 'at' | 'read'>, now: number): AppState => ({
  ...s,
  notifications: [...s.notifications.slice(-79), { ...n, id: uid('ntf'), at: now, read: false }],
});

const bump = (s: AppState, key: string, by = 1): AppState => ({ ...s, counters: { ...s.counters, [key]: (s.counters[key] ?? 0) + by } });

function viewFor(state: AppState, skillId: string, now: number): SkillView {
  const skill = catalog.skill(skillId)!;
  const elig = eligibilityOf(state);
  return deriveView(skill, state.estimates[skillId], skillRelevance(skill, elig[skill.subjectId]), now);
}

/* ------------------------------------------------------------------ */

const answerProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type !== 'QUESTION_ANSWERED') return state;
  const q = e.question;
  const ev = e.evaluation;
  const skillId = q.skillId;
  const before = viewFor(state, skillId, now);
  const prevEst = state.estimates[skillId];
  const recent = state.exposure[skillId] ?? [];
  const repeat = recent.slice(-15).includes(q.fingerprint);

  // 1. Attempt
  const attempt: Attempt = {
    id: uid('att'), at: now, skillId, subjectId: q.subjectId, questionId: q.id, fingerprint: q.fingerprint, familyId: q.familyId,
    kind: q.kind, difficulty: q.difficulty, correct: ev.correct, score: ev.score, responseMs: e.responseMs, hintsUsed: e.hintsUsed,
    confidence: e.confidence, source: e.source, sessionId: e.sessionId, misconceptionId: ev.misconceptionId, errorType: ev.errorType, prompt: q.prompt.slice(0, 200),
  };

  // 2. Evidence → estimate (mastery, confidence, forgetting memory)
  const est = applyEvidence(prevEst, {
    skillId, difficulty: q.difficulty, correct: ev.correct, score: ev.score, hintsUsed: e.hintsUsed, responseMs: e.responseMs,
    confidence: e.confidence, source: e.source, fingerprint: q.fingerprint, guess: guessRate(q), at: now, misconceptionId: ev.misconceptionId,
  });
  let s: AppState = {
    ...state,
    attempts: [...state.attempts.slice(-2999), attempt],
    estimates: { ...state.estimates, [skillId]: est },
    exposure: { ...state.exposure, [skillId]: [...recent.slice(-39), q.fingerprint] },
  };

  // Non-evidence annotation: a hard success suggests (not proves) its prerequisites are in place.
  if (ev.correct && e.hintsUsed === 0 && q.difficulty >= 4) {
    const upd = { ...s.estimates };
    let changed = false;
    for (const p of catalog.prerequisites(skillId)) {
      const pe = upd[p];
      if (!pe || pe.evidenceCount === 0) {
        upd[p] = { ...(pe ?? newEstimate(p)), inferredSupport: { fromSkillId: skillId, at: now } };
        changed = true;
      }
    }
    if (changed) s = { ...s, estimates: upd };
  }

  // 3. Activity, streak, personal bests
  const dk = dayKey(now);
  const day = s.activity[dk] ?? { attempts: 0, correct: 0, xp: 0, ms: 0 };
  s = { ...s, activity: { ...s.activity, [dk]: { ...day, attempts: day.attempts + 1, correct: day.correct + (ev.correct ? 1 : 0), ms: day.ms + Math.min(e.responseMs, 10 * 60_000) } } };
  if (s.streak.lastDay !== dk) {
    const yesterday = dayKey(now - 86_400_000);
    const current = s.streak.lastDay === yesterday ? s.streak.current + 1 : 1;
    s = { ...s, streak: { current, best: Math.max(s.streak.best, current), lastDay: dk } };
  }
  const run = (() => {
    let n = 0;
    for (let i = s.attempts.length - 1; i >= 0 && s.attempts[i].correct; i--) n++;
    return n;
  })();
  s = {
    ...s,
    personalBests: {
      ...s.personalBests,
      longestCorrectStreak: Math.max(s.personalBests.longestCorrectStreak, run),
      hardestCorrect: ev.correct && e.hintsUsed === 0 ? Math.max(s.personalBests.hardestCorrect, q.difficulty) : s.personalBests.hardestCorrect,
      mostQuestionsInDay: Math.max(s.personalBests.mostQuestionsInDay, (s.activity[dk]?.attempts ?? 0)),
    },
  };
  if (ev.correct) s = bump(s, 'correct');
  const ability = before.ability;
  if (ev.correct && ability !== null && q.difficulty >= ability + 0.5) s = bump(s, 'stretch-correct');
  if (ev.correct && q.difficulty === 7 && e.hintsUsed === 0) s = bump(s, 'd7-independent');

  // 4. Mistakes and recovery
  if (!ev.correct && e.source !== 'diagnostic') {
    const views = viewsOf(s, now);
    const { mistake, isNew } = createOrUpdateMistake(s.mistakes, q, e.response, ev, views, e.source, now, e.reasoning);
    s = { ...s, mistakes: isNew ? [...s.mistakes, mistake] : s.mistakes.map((m) => (m.id === mistake.id ? mistake : m)) };
    emit(isNew ? { type: 'MISTAKE_CREATED', mistake } : { type: 'MISTAKE_UPDATED', mistake });
    if (mistake.diagnosis === 'prerequisite-gap' && mistake.suspectedPrerequisites[0]) {
      const p = mistake.suspectedPrerequisites[0];
      emit({ type: 'PREREQUISITE_GAP_DETECTED', skillId, prerequisiteId: p, blocks: catalog.descendants(p).slice(0, 12) });
    }
  }
  if (ev.correct) {
    const { mistakes, resolved } = progressRecovery(s.mistakes, skillId, q.difficulty, e.hintsUsed, now);
    s = { ...s, mistakes };
    for (const m of resolved) emit({ type: 'MISTAKE_RESOLVED', mistakeId: m.id, skillId: m.skillId });
    // recovering a weak prerequisite
    if (before.status === 'WEAK' && q.difficulty >= 3) {
      s = bump(s, 'prerequisite-recovered');
      emit({ type: 'QUESTS_REFRESHED' });
    }
  }

  // 5. Estimate change events
  const after = viewFor(s, skillId, now);
  if (before.evidenceCount === 0) emit({ type: 'SKILL_ASSESSED', skillId });
  emit({ type: 'SKILL_ESTIMATE_UPDATED', skillId, before, after });

  // 6. XP for the answer
  const xp = xpForAnswer(q, ev.correct, prevEst && prevEst.evidenceCount ? mean(prevEst.posterior) : null, e.hintsUsed, e.source, repeat);
  if (xp.amount > 0) emit({ type: 'XP_AWARDED', amount: xp.amount, reason: xp.reason, source: e.source, skillId });

  return s;
};

const estimateChangeProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type !== 'SKILL_ESTIMATE_UPDATED') return state;
  let s = state;
  const gain = e.after.mastery - e.before.mastery;
  if (gain > 0.02) emit({ type: 'XP_AWARDED', amount: Math.round(gain * 60), reason: L(`Mastery +${Math.round(gain * 100)} % in ${catalog.skill(e.skillId)!.name}`, `Mestring +${Math.round(gain * 100)} % i ${catalog.skill(e.skillId)!.name}`), source: 'mastery', skillId: e.skillId });
  if (e.after.status === 'MASTERED' && e.before.status !== 'MASTERED') emit({ type: 'SKILL_MASTERED', skillId: e.skillId });
  if (e.before.status === 'WEAK' && (e.after.status === 'DEVELOPING' || e.after.status === 'PROFICIENT')) {
    s = bump(s, 'gap-repaired');
    s = notify(s, { kind: 'mastery', title: LL('Basics fixed!', 'Grunnlaget er fikset!', { detailed: ['Foundation repaired', 'Grunnmuren er reparert'] }), body: L(`${catalog.skill(e.skillId)!.name} is no longer weak.`, `${catalog.skill(e.skillId)!.name} er ikke lenger svak.`), link: `/skill/${e.skillId}` }, now);
  }
  return s;
};

const masteryProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type === 'SKILL_MASTERED') {
    const skill = catalog.skill(e.skillId)!;
    let s = bump(state, 'skills-mastered');
    s = notify(s, { kind: 'mastery', title: L('Skill mastered', 'Ferdighet mestret'), body: LL(`${skill.name} is MASTERED — you showed it!`, `${skill.name} er MESTRET — du viste at du kan det!`, { detailed: [`${skill.name} reached MASTERED from demonstrated evidence.`, `${skill.name} er MESTRET ut fra det du har vist.`] }), link: `/skill/${skill.id}` }, now);
    emit({ type: 'XP_AWARDED', amount: 50, reason: L(`Mastered ${skill.name}`, `Mestret ${skill.name}`), source: 'mastery', skillId: skill.id });
    const views = viewsOf(s, now);
    for (const d of catalog.dependents(skill.id)) {
      const pre = catalog.prerequisites(d);
      const ready = pre.every((p) => views[p]?.status === 'MASTERED' || views[p]?.status === 'PROFICIENT');
      if (ready && views[d] && views[d].evidenceCount === 0) emit({ type: 'SKILL_UNLOCKED', skillId: d, becauseOf: skill.id });
    }
    return s;
  }
  if (e.type === 'SKILL_UNLOCKED') {
    const skill = catalog.skill(e.skillId)!;
    return notify(state, { kind: 'unlock', title: L('Path unlocked', 'Ny sti åpnet'), body: LL(`${skill.name} — unlocked! You've shown what it needs.`, `${skill.name} — låst opp! Du har vist det som trengs.`, { detailed: [`${skill.name} — its prerequisites are now demonstrated.`, `${skill.name} — forkunnskapene er nå vist.`] }), link: `/learn/${skill.id}?mode=lesson` }, now);
  }
  if (e.type === 'PREREQUISITE_GAP_DETECTED') {
    const p = catalog.skill(e.prerequisiteId);
    if (!p) return state;
    const recentGap = state.notifications.slice(-10).some((n) => n.kind === 'gap' && n.body.includes(p.name));
    if (recentGap) return state;
    return notify(state, { kind: 'gap', title: LL('Missing piece found', 'Manglende brikke funnet', { detailed: ['Prerequisite gap detected', 'Hull i forkunnskaper funnet'] }), body: LL(`${p.name} needs practice and blocks ${e.blocks.length} later skill${e.blocks.length === 1 ? '' : 's'}.`, `${p.name} trenger øving og stopper ${e.blocks.length} senere ferdighet${e.blocks.length === 1 ? '' : 'er'}.`, { detailed: [`${p.name} looks weak and blocks ${e.blocks.length} later skill${e.blocks.length === 1 ? '' : 's'}.`, `${p.name} ser svak ut og stopper ${e.blocks.length} senere ferdighet${e.blocks.length === 1 ? '' : 'er'}.`] }), link: `/learn/${p.id}?mode=recovery` }, now);
  }
  return state;
};

const mistakeProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type === 'MISTAKE_RESOLVED') {
    const s = bump(state, 'mistakes-resolved');
    emit({ type: 'XP_AWARDED', amount: 20, reason: LL('Mistake fixed!', 'Feil rettet!', { detailed: ['Mistake recovered and re-tested', 'Feil rettet og testet på nytt'] }), source: 'recovery', skillId: e.skillId });
    return notify(s, { kind: 'system', title: L('Mistake resolved', 'Feilen er løst'), body: L(`Re-test passed for ${catalog.skill(e.skillId)?.name}.`, `Ny test bestått i ${catalog.skill(e.skillId)?.name}.`), link: '/mistakes' }, now);
  }
  return state;
};

const xpProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type !== 'XP_AWARDED') return state;
  const before = levelInfo(state.xp.total);
  const total = state.xp.total + e.amount;
  const after = levelInfo(total);
  const dk = dayKey(now);
  const day = state.activity[dk] ?? { attempts: 0, correct: 0, xp: 0, ms: 0 };
  const s: AppState = {
    ...state,
    xp: { total, events: [...state.xp.events.slice(-1499), { id: uid('xp'), at: now, amount: e.amount, reason: e.reason, source: e.source, skillId: e.skillId }] },
    activity: { ...state.activity, [dk]: { ...day, xp: day.xp + e.amount } },
  };
  if (after.level > before.level) emit({ type: 'LEVEL_UP', from: before.level, to: after.level });
  return s;
};

const levelProcessor: Processor = (state, e, { now }) => {
  if (e.type !== 'LEVEL_UP') return state;
  const info = levelInfo(state.xp.total);
  return notify(state, { kind: 'levelup', title: L(`Level ${e.to} — ${info.title}`, `Nivå ${e.to} — ${info.title}`), body: LL('You earned it by learning for real.', 'Du har fortjent det ved å lære på ordentlig.', { detailed: ['Your progression reflects demonstrated learning.', 'Fremgangen din bygger på læring du har vist.'] }) }, now);
};

const METRIC_FOR: Partial<Record<StudioEvent['type'], string>> = {
  REVIEW_COMPLETED: 'reviews-completed',
  MISTAKE_RESOLVED: 'mistakes-resolved',
  SKILL_MASTERED: 'skills-mastered',
  BOSS_DEFEATED: 'boss-defeated',
  CALIBRATION_COMPLETED: 'calibration-completed',
  EXAM_SUBMITTED: 'exam-submitted',
  NOTE_SAVED: 'notes-written',
  SKILL_ASSESSED: 'new-skill-assessed',
};

const questProcessor: Processor = (state, e, { emit, now }) => {
  let metric: string | undefined = METRIC_FOR[e.type];
  let amount = 1;
  if (e.type === 'QUESTION_ANSWERED') {
    if (!e.evaluation.correct) return state;
    const est = state.estimates[e.question.skillId];
    // stretch computed against the estimate before this answer is not available here; use counters delta
    metric = 'questions-correct';
    void est;
  }
  if (e.type === 'FEYNMAN_SUBMITTED') {
    if (e.record.overall < 0.7) return state;
    metric = 'feynman-completed';
  }
  if (e.type === 'XP_AWARDED' && e.reason.startsWith('Stretch')) metric = 'stretch-correct';
  if (e.type === 'QUESTS_REFRESHED') metric = 'prerequisite-recovered';
  if (!metric) return state;
  let changed = false;
  const quests = state.quests.map((q) => {
    if (q.completedAt || q.metric !== metric || (q.expiresAt && q.expiresAt < now)) return q;
    changed = true;
    const progress = Math.min(q.target, q.progress + amount);
    if (progress >= q.target) {
      emit({ type: 'QUEST_COMPLETED', questId: q.id });
      emit({ type: 'XP_AWARDED', amount: q.rewardXp, reason: L(`Quest complete: ${q.title}`, `Oppdrag fullført: ${q.title}`), source: 'quest' });
      return { ...q, progress, completedAt: now };
    }
    return { ...q, progress };
  });
  amount = 1;
  return changed ? { ...state, quests } : state;
};

const questNotify: Processor = (state, e, { now }) => {
  if (e.type !== 'QUEST_COMPLETED') return state;
  const q = state.quests.find((x) => x.id === e.questId);
  return q ? notify(state, { kind: 'quest', title: L('Quest complete', 'Oppdrag fullført'), body: `${q.title} (+${q.rewardXp} XP)`, link: '/quests' }, now) : state;
};

const reviewProcessor: Processor = (state, e, { emit, now }) => {
  if (e.type !== 'REVIEW_COMPLETED') return state;
  let s: AppState = { ...state, reviewLog: [...state.reviewLog.slice(-2999), { id: uid('rv'), at: now, itemType: e.itemType, itemId: e.itemId, rating: e.rating, intervalDays: 0 }] };
  if (e.itemType === 'card') {
    s = { ...s, flashcards: s.flashcards.map((c) => (c.id === e.itemId ? { ...c, memory: review(c.memory, e.rating, now) } : c)) };
  } else if (e.itemType === 'locus' && e.palaceId) {
    s = { ...s, palaces: s.palaces.map((p) => (p.id === e.palaceId ? { ...p, loci: p.loci.map((l) => (l.id === e.itemId ? { ...l, memory: review(l.memory, e.rating, now) } : l)) } : p)) };
  }
  emit({ type: 'XP_AWARDED', amount: e.rating === 'again' ? 1 : 3, reason: L('Review completed', 'Repetisjon fullført'), source: 'review' });
  return s;
};

const calibrationProcessor: Processor = (state, e, { emit, now }) => {
  switch (e.type) {
    case 'CALIBRATION_STARTED':
    case 'CALIBRATION_RUN_UPDATED':
      return { ...state, calibration: { ...state.calibration, active: e.run } };
    case 'CALIBRATION_QUESTION_ANSWERED':
      return { ...state, calibration: { ...state.calibration, active: e.run, log: [...state.calibration.log.slice(-399), e.entry] } };
    case 'CALIBRATION_COMPLETED': {
      emit({ type: 'XP_AWARDED', amount: 30, reason: LL(`Level check complete: ${catalog.subject(e.subjectId)?.name}`, `Nivåsjekk fullført: ${catalog.subject(e.subjectId)?.name}`, { detailed: [`Calibration complete: ${catalog.subject(e.subjectId)?.name}`, `Kartlegging fullført: ${catalog.subject(e.subjectId)?.name}`] }), source: 'calibration' });
      const s = { ...state, calibration: { ...state.calibration, summaries: { ...state.calibration.summaries, [e.subjectId]: e.summary } } };
      return notify(s, { kind: 'system', title: LL('Profile ready', 'Profilen er klar', { detailed: ['Profile calibrated', 'Profilen er kartlagt'] }), body: L(`${catalog.subject(e.subjectId)?.name}: ${e.summary.questions} answers analysed.`, `${catalog.subject(e.subjectId)?.name}: ${e.summary.questions} svar analysert.`), link: '/profile' }, now);
    }
    case 'CALIBRATION_FINISHED':
    case 'CALIBRATION_ABANDONED':
      return { ...state, calibration: { ...state.calibration, active: null } };
    default:
      return state;
  }
};

const entityProcessor: Processor = (state, e, { emit, now }) => {
  const upsert = <T extends { id: string }>(list: T[], item: T) => (list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [...list, item]);
  switch (e.type) {
    case 'PROFILE_CREATED':
      return { ...state, profile: e.profile };
    case 'PROFILE_UPDATED':
      return state.profile ? { ...state, profile: { ...state.profile, ...e.patch } } : state;
    case 'EDUCATION_CHANGED': {
      if (!state.profile) return state;
      // Stage/pathway changes only change RELEVANCE. Estimates are untouched.
      const from = state.profile.education;
      const s = { ...state, profile: { ...state.profile, education: e.education, educationHistory: [...state.profile.educationHistory, { at: now, from, to: e.education }] } };
      const views = viewsOf(s, now);
      const newlyRelevant = Object.values(views).filter((v) => (v.relevance === 'core') && v.evidenceCount === 0).length;
      return notify(s, { kind: 'system', title: L('Curriculum updated', 'Læreplanen er oppdatert'), body: LL(`Everything you showed is kept. ${newlyRelevant} key skills are new and ready to try.`, `Alt du har vist er beholdt. ${newlyRelevant} viktige ferdigheter er nye og klare til å prøves.`, { detailed: [`Evidence was preserved. ${newlyRelevant} core skills are unknown and can be assessed.`, `Alt du har vist er beholdt. ${newlyRelevant} kjerneferdigheter er ukjente og kan kartlegges.`] }), link: '/profile' }, now);
    }
    case 'NOTE_SAVED':
      return { ...state, notes: upsert(state.notes, e.note) };
    case 'NOTE_DELETED':
      return { ...state, notes: state.notes.filter((n) => n.id !== e.id) };
    case 'PLANNER_SAVED':
      return { ...state, planner: upsert(state.planner, e.event) };
    case 'PLANNER_DELETED':
      return { ...state, planner: state.planner.filter((p) => p.id !== e.id) };
    case 'GOAL_SAVED':
      return { ...state, goals: upsert(state.goals, e.goal) };
    case 'GOAL_DELETED':
      return { ...state, goals: state.goals.filter((g) => g.id !== e.id) };
    case 'EXAM_SAVED':
      return { ...state, exams: upsert(state.exams, e.exam) };
    case 'EXAM_DELETED':
      return { ...state, exams: state.exams.filter((x) => x.id !== e.id) };
    case 'PALACE_SAVED':
      return { ...state, palaces: upsert(state.palaces, e.palace) };
    case 'PALACE_DELETED':
      return { ...state, palaces: state.palaces.filter((p) => p.id !== e.id) };
    case 'FLASHCARD_SAVED':
      return { ...state, flashcards: upsert(state.flashcards, e.card) };
    case 'FLASHCARD_DELETED':
      return { ...state, flashcards: state.flashcards.filter((c) => c.id !== e.id) };
    case 'CONVERSATION_SAVED':
      return { ...state, conversations: upsert(state.conversations, e.conversation).slice(-30) };
    case 'COURSE_CREATED':
      return { ...state, courses: [...state.courses, e.course] };
    case 'COURSE_LESSON_COMPLETED': {
      let completedNow = false;
      const courses = state.courses.map((c) => {
        if (c.id !== e.courseId) return c;
        const modules = c.modules.map((m) => ({ ...m, lessons: m.lessons.map((l) => (l.id === e.lessonId && !l.completedAt ? { ...l, completedAt: now } : l)) }));
        const all = modules.flatMap((m) => m.lessons).every((l) => l.completedAt);
        if (all && !c.completedAt) completedNow = true;
        return { ...c, modules, completedAt: all ? c.completedAt ?? now : c.completedAt };
      });
      if (completedNow) emit({ type: 'COURSE_COMPLETED', courseId: e.courseId });
      return { ...state, courses };
    }
    case 'COURSE_LESSON_QUESTIONS':
      return {
        ...state,
        courses: state.courses.map((c) => (c.id !== e.courseId ? c : {
          ...c,
          modules: c.modules.map((m) => ({ ...m, lessons: m.lessons.map((l) => (l.id === e.lessonId ? { ...l, aiQuestions: e.questions.slice(0, 12) } : l)) })),
        })),
      };
    case 'COURSE_COMPLETED':
      emit({ type: 'XP_AWARDED', amount: 100, reason: L('Course completed', 'Kurs fullført'), source: 'course' });
      return notify(state, { kind: 'achievement', title: L('Course completed', 'Kurs fullført'), body: state.courses.find((c) => c.id === e.courseId)?.title ?? '', link: `/courses/${e.courseId}` }, now);
    case 'BOSS_ATTEMPTED': {
      const rec = state.bosses[e.bossId] ?? { attempts: 0, bestDamage: 0 };
      const next = { ...rec, attempts: rec.attempts + 1, bestDamage: Math.max(rec.bestDamage, e.damage), defeatedAt: e.defeated ? rec.defeatedAt ?? now : rec.defeatedAt };
      if (e.defeated && !rec.defeatedAt) emit({ type: 'BOSS_DEFEATED', bossId: e.bossId });
      return { ...state, bosses: { ...state.bosses, [e.bossId]: next } };
    }
    case 'BOSS_DEFEATED': {
      const boss = BOSSES.find((b) => b.id === e.bossId);
      if (boss) emit({ type: 'XP_AWARDED', amount: boss.rewardXp, reason: L(`Defeated ${boss.name}`, `Beseiret ${boss.name}`), source: 'boss' });
      return notify(state, { kind: 'boss', title: L('Boss defeated', 'Bossen er beseiret'), body: boss ? L(`${boss.name} has fallen.`, `${boss.name} har falt.`) : '', link: '/quests' }, now);
    }
    case 'EXAM_SUBMITTED': {
      const avg = e.result.scores.reduce((a, b) => a + b, 0) / Math.max(1, e.result.scores.length);
      emit({ type: 'XP_AWARDED', amount: Math.round(10 + 30 * avg), reason: L(`Exam session: ${Math.round(avg * 100)} %`, `Prøve: ${Math.round(avg * 100)} %`), source: 'exam' });
      return { ...state, examResults: [...state.examResults, e.result], personalBests: { ...state.personalBests, bestExamScore: Math.max(state.personalBests.bestExamScore, avg) } };
    }
    case 'FEYNMAN_SUBMITTED': {
      if (e.record.overall >= 0.7) emit({ type: 'XP_AWARDED', amount: 15, reason: L('Feynman explanation passed', 'Feynman-forklaring bestått'), source: 'feynman', skillId: e.record.skillId });
      // An explanation is real but weak evidence (source weight 0.6) of conceptual understanding.
      const skill = catalog.skill(e.record.skillId);
      if (!skill) return { ...state, feynman: [...state.feynman, e.record] };
      const est = applyEvidence(state.estimates[skill.id], {
        skillId: skill.id, difficulty: Math.max(skill.difficultyRange[0], skill.masteryTarget - 2), correct: e.record.overall >= 0.7,
        score: e.record.overall, hintsUsed: 0, responseMs: 120_000, source: 'feynman', fingerprint: `feynman|${e.record.id}`, guess: 0, at: now,
      });
      return { ...state, feynman: [...state.feynman, e.record], estimates: { ...state.estimates, [skill.id]: est } };
    }
    case 'WRITING_ANALYSED':
      return { ...state, writing: [...state.writing, e.record] };
    case 'ASSESSMENT_SAVED':
      // re-saving the same report (e.g. after an AI-graded aim) replaces it
      return { ...state, assessments: [...state.assessments.filter((a) => a.id !== e.record.id), e.record].slice(-40) };
    case 'SESSION_STARTED':
      return { ...state, sessions: [...state.sessions.slice(-199), { id: e.sessionId, mode: e.mode, startedAt: now, skillIds: e.skillIds, attempts: 0, correct: 0, xp: 0 }] };
    case 'SESSION_ENDED':
      return { ...state, sessions: state.sessions.map((x) => (x.id === e.sessionId ? { ...x, endedAt: now } : x)) };
    case 'NOTIFICATIONS_READ':
      return { ...state, notifications: state.notifications.map((n) => ({ ...n, read: true })) };
    case 'SETTINGS_CHANGED':
      return { ...state, settings: { ...state.settings, ...e.patch } };
    default:
      return state;
  }
};

const sessionCounter: Processor = (state, e) => {
  if (e.type !== 'QUESTION_ANSWERED' || !e.sessionId) return state;
  return { ...state, sessions: state.sessions.map((x) => (x.id === e.sessionId ? { ...x, attempts: x.attempts + 1, correct: x.correct + (e.evaluation.correct ? 1 : 0) } : x)) };
};

/** Runs last: quests refresh and achievements react to the new state. */
const housekeeping: Processor = (state, e, { emit, now }) => {
  if (!state.profile) return state;
  let s = state;
  if (e.type === 'TICK' || e.type === 'PROFILE_CREATED' || e.type === 'CALIBRATION_FINISHED' || e.type === 'QUESTS_REFRESHED') {
    const q = refreshQuests(s, viewsOf(s, now), now);
    if (q) s = { ...s, quests: q };
  }
  if (e.type === 'ACHIEVEMENT_UNLOCKED' || e.type === 'TICK' || e.type === 'XP_AWARDED' || e.type === 'NOTE_SAVED' || e.type === 'PALACE_SAVED' || e.type === 'WRITING_ANALYSED' || e.type === 'ASSESSMENT_SAVED' || e.type === 'REVIEW_COMPLETED') {
    const ids = checkAchievements(s, viewsOf(s, now));
    if (ids.length) {
      s = { ...s, achievements: { ...s.achievements, ...Object.fromEntries(ids.map((id) => [id, now])) } };
      for (const id of ids) {
        emit({ type: 'ACHIEVEMENT_UNLOCKED', id });
        const a = achievementById(id);
        if (a) s = notify(s, { kind: 'achievement', title: a.title, body: a.description, link: '/achievements' }, now);
      }
    }
  }
  return s;
};

export function installProcessors(store: Store) {
  [
    entityProcessor, answerProcessor, sessionCounter, estimateChangeProcessor, masteryProcessor, mistakeProcessor,
    reviewProcessor, calibrationProcessor, xpProcessor, levelProcessor, questProcessor, questNotify, housekeeping,
  ].forEach((p) => store.use(p));
}
